import { useClassStore } from '@Utils/useClassStore';
import { inject, injectable } from 'inversify';
import { action, observable, runInAction } from 'mobx';

import container from '../../ioc/ioc';

import FirebaseService from '~gateways/analytics/firebase-service';
import { AnalyticsStore } from '~stores/analytics';
import AuthStore from '~stores/authentication';
import OrderHistoryStore from '~stores/order-history';
import ProductsStore from '~stores/products';
import SmartBrowserStore from '~stores/smart-browser';
import { LoginMethods } from '~types/analytics';
import type { IError, IUser } from '~types/models-types';
import { PendingAuthenticatedActions } from '~types/views-types';

@injectable()
class AuthPresenter {
  @inject(ProductsStore)
  private ProductsStore!: ProductsStore;
  @inject(AuthStore)
  private AuthStore!: AuthStore;
  @inject(OrderHistoryStore)
  private OrderHistoryStore!: OrderHistoryStore;
  @inject(SmartBrowserStore)
  private SmartBrowserStore!: SmartBrowserStore;
  @inject(AnalyticsStore) private _analyticsStore!: AnalyticsStore;

  @observable error: IError | null = this.AuthStore.error;

  @action
  public readonly handleShareProductAuthCheckClick = () => {
    if (!this.AuthStore.currentUser) {
      this.AuthStore.pendingAuthenticatedAction =
        PendingAuthenticatedActions.shareproduct;
      this.handleLoginClick();
      return;
    }
    this.ProductsStore.setUiShareProductByEmailOverlayOpen(true);
  };

  @action
  public readonly handleLoginSubmitAsync = async (
    username: string,
    password: string
  ) => {
    const loginResult = await this.AuthStore.authenticateAsync(
      username,
      password
    );

    // For some reason, serviceStack is returning `TypeError: Right hand side of instanceof is not an object` when auth details are incorrect
    // As a workaround, we'll check if the result is falsy and show an error message if it is
    if (!loginResult) {
      this.AuthStore.error = {
        errorCode: '',
        message: 'The email address or password is incorrect.',
        success: false,
      };
      return;
    }

    if (loginResult?.errorCode) {
      this.AuthStore.error = loginResult as IError;
      return;
    }
    // rebuild the nav map so that the menu can reflect the logged-in status
    runInAction(async () => {
      this.AuthStore.toggleLogin({ setting: false });
      this.AuthStore.currentUser = loginResult as IUser;
      runInAction(async () => {
        if (this.AuthStore.pendingAuthenticatedAction) {
          switch (this.AuthStore.pendingAuthenticatedAction) {
            case PendingAuthenticatedActions.tagmenu:
              this.ProductsStore.uiTagMenuOpen = true;
              FirebaseService.login({
                method: LoginMethods.favourite,
                user: this.AuthStore.currentUser
                  ? this.AuthStore.currentUser.id
                  : '',
              }).then();

              break;
            case PendingAuthenticatedActions.mylists:
              FirebaseService.login({
                method: LoginMethods.saved,
                user: this.AuthStore.currentUser
                  ? this.AuthStore.currentUser.id
                  : '',
              }).then();

              break;
            case PendingAuthenticatedActions.myorderhistory:
              this.OrderHistoryStore.initOrderHistory().then();
              this.AuthStore.pendingAuthenticatedAction = null;
              FirebaseService.login({
                method: LoginMethods.orders,
                user: this.AuthStore.currentUser
                  ? this.AuthStore.currentUser.id
                  : '',
              }).then();

              break;
            case PendingAuthenticatedActions.bylist:
              FirebaseService.login({
                method: LoginMethods.favourite,
                user: this.AuthStore.currentUser
                  ? this.AuthStore.currentUser.id
                  : '',
              }).then();

              break;
            case PendingAuthenticatedActions.mytrips:
              this.AuthStore.pendingAuthenticatedAction = null;
              FirebaseService.login({
                method: LoginMethods.history,
                user: this.AuthStore.currentUser
                  ? this.AuthStore.currentUser.id
                  : '',
              }).then();

              break;
            case PendingAuthenticatedActions.shareproduct:
              this.ProductsStore.setUiShareProductByEmailOverlayOpen(true);
              FirebaseService.login({
                method: LoginMethods.share,
                user: this.AuthStore.currentUser
                  ? this.AuthStore.currentUser.id
                  : '',
              }).then();

              break;
            case PendingAuthenticatedActions.smartBrowser:
              this.SmartBrowserStore.postLogin().then();
              break;
            case PendingAuthenticatedActions.accountmanagement:
              this.ProductsStore.setUiShareProductByEmailOverlayOpen(true);
              FirebaseService.login({
                method: LoginMethods.login,
                user: this.AuthStore.currentUser
                  ? this.AuthStore.currentUser.id
                  : '',
              }).then();
              break;
            default:
              this._analyticsStore.logLogin({
                userId: this.AuthStore.currentUser?.id!,
                context: 'login',
              });
          }
        }
      }).then();
    }).then();
  };

  @action
  public readonly handleLoginClick = () => {
    this.AuthStore.toggleLogin({ setting: true, method: 'login' });
  };

  @action
  public readonly handleLogOutClickAsync = async () => {
    this.AuthStore.logOut().then();
  };
}

const useAuthPresenter = () =>
  useClassStore<AuthPresenter>(container.get(AuthPresenter));

export default useAuthPresenter;
